import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type ICallPayload } from '@/types/ICallPayload';
import { RESOLVED_CALL_TTL_MS } from '@/constants/socketTimings';

interface IResolveCallPayload {
  /** Нормализованный телефон (только цифры). */
  phone: string;
  /** Время терминального события, мс. */
  occurredAt: number;
}

export interface ICallsState {
  /** Звонок, открытый в модальном окне. */
  activeCall: ICallPayload | null;
  isModalOpen: boolean;
  /**
   * Реестр закрытых звонков: телефон → время терминального события, мс.
   * Нужен на случай нарушенного порядка доставки — `ACCEPTED` может прийти
   * раньше `INCOMING`, и тогда входящий тост показывать уже не надо.
   * Эфемерный, не персистится, чистится по TTL при каждой записи.
   */
  resolvedCalls: Record<string, number>;
}

const initialState: ICallsState = {
  activeCall: null,
  isModalOpen: false,
  resolvedCalls: {}
};

const calls = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    /** Открытие модалки — только вручную, кликом по уведомлению. */
    openCallModal(state, action: PayloadAction<ICallPayload>) {
      state.activeCall = action.payload;
      state.isModalOpen = true;
    },
    /**
     * Закрытие модалки. `activeCall` намеренно сохраняем: react-bootstrap
     * анимирует закрытие, и обнуление данных вызвало бы моргание пустой
     * модалкой. Сбрасывает всё `clearCalls`.
     */
    closeCallModal(state) {
      state.isModalOpen = false;
    },
    /** Помечает звонок закрытым и попутно чистит просроченные записи. */
    resolveCall(state, action: PayloadAction<IResolveCallPayload>) {
      const { phone, occurredAt } = action.payload;
      const threshold = Date.now() - RESOLVED_CALL_TTL_MS;

      const fresh: Record<string, number> = {};
      Object.entries(state.resolvedCalls).forEach(([key, value]) => {
        if (value > threshold) {
          fresh[key] = value;
        }
      });

      const previous = fresh[phone];
      fresh[phone] =
        previous === undefined ? occurredAt : Math.max(previous, occurredAt);

      state.resolvedCalls = fresh;
    },
    /** Полный сброс состояния звонков (логаут). */
    clearCalls(state) {
      state.activeCall = null;
      state.isModalOpen = false;
      state.resolvedCalls = {};
    }
  }
});

export const { openCallModal, closeCallModal, resolveCall, clearCalls } =
  calls.actions;

export default calls.reducer;
